// src/services/queries/season-team.ts

import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QueryParams, SeasonTeam } from '@/model';
import { SeasonTeamConverter } from '@/converters/season-team';
import { SeasonTeamService } from '../instances/season-team';
import {
  PostSeasonTeamRequest,
  PostSeasonTeamsBatchRequest,
} from '../request/season-team';

/**
 * GET /season-teams — lista paginada.
 *
 * Aceita `enabled` (default true) para evitar disparos com filtro vazio que
 * trariam a lista global da BU. Passe `enabled: !!seasonUuid` (ou outro guard)
 * sempre que a query depender de um filtro que ainda pode não ter sido
 * escolhido pelo usuário.
 */
export const useSeasonTeams = (params?: QueryParams, enabled: boolean = true) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['GET_SEASON_TEAMS', params], [params]);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const [err, data] = await SeasonTeamService.getSeasonTeams(params);
      if (err) throw err;
      return {
        data: SeasonTeamConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      } as { data: SeasonTeam[]; meta: typeof data.meta };
    },
    enabled,
    refetchOnWindowFocus: false,
  });

  const forceRefetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return useMemo(() => ({ ...query, forceRefetch }), [query, forceRefetch]);
};

/** POST /season-teams — associa um time. */
export const usePostSeasonTeamMutation = () => {
  return useMutation({
    mutationKey: ['POST_SEASON_TEAM'],
    mutationFn: async (payload: PostSeasonTeamRequest) => {
      const [err, data] = await SeasonTeamService.postSeasonTeam(payload);
      if (err) throw err;
      return SeasonTeamConverter.toDomainFromPostResponse(data);
    },
  });
};

/** POST /season-teams/batch — associa vários. */
export const usePostSeasonTeamsBatchMutation = () => {
  return useMutation({
    mutationKey: ['POST_SEASON_TEAMS_BATCH'],
    mutationFn: async (payload: PostSeasonTeamsBatchRequest) => {
      const [err, data] = await SeasonTeamService.postSeasonTeamsBatch(payload);
      if (err) throw err;
      return SeasonTeamConverter.toDomainFromPostBatchResponse(data);
    },
  });
};

/** DELETE /season-teams/:id. */
export const useDeleteSeasonTeamMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_SEASON_TEAM'],
    mutationFn: async (id: string) => {
      const [err] = await SeasonTeamService.deleteSeasonTeam(id);
      if (err) throw err;
      return;
    },
  });
};
